import React, { useEffect, useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, Webhook, ArrowRight } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { TemplateFormData, WebhookConfig, EventMapping, FieldMapping } from '../TemplateWizard';

interface WebhookConfigStepProps {
  data: TemplateFormData;
  onChange: (updates: Partial<TemplateFormData>) => void;
  onValidate: (isValid: boolean) => void;
}

const signatureTypes = [
  { value: 'HMAC_SHA256', label: 'HMAC-SHA256', description: 'Mais comum e seguro' },
  { value: 'HMAC_SHA1', label: 'HMAC-SHA1', description: 'Compatibilidade legada' },
  { value: 'RSA', label: 'RSA Signature', description: 'Assinatura assimétrica' },
  { value: 'NONE', label: 'Sem validação', description: 'Não recomendado' },
] as const;

const emptyEventMapping: EventMapping = {
  providerEvent: '',
  hubEvent: '',
  payloadMapping: [],
};

const hubEvents = [
  'CHARGE_CREATED',
  'CHARGE_COMPLETED',
  'CHARGE_EXPIRED',
  'CHARGE_CANCELLED',
  'PAYMENT_CREATED',
  'PAYMENT_CONFIRMED',
  'PAYMENT_FAILED',
  'TRANSFER_COMPLETED',
  'TRANSFER_FAILED',
  'BALANCE_UPDATED',
];

export const WebhookConfigStep: React.FC<WebhookConfigStepProps> = ({
  data,
  onChange,
  onValidate,
}) => {
  const [expandedEvent, setExpandedEvent] = useState<number | null>(null);
  const webhookConfig = data.webhookConfig;

  // Update webhook config
  const updateWebhookConfig = (updates: Partial<WebhookConfig>) => {
    onChange({
      webhookConfig: { ...webhookConfig, ...updates },
    });
  };

  // Add event mapping
  const addEventMapping = () => {
    updateWebhookConfig({
      eventMappings: [...webhookConfig.eventMappings, { ...emptyEventMapping }],
    });
    setExpandedEvent(webhookConfig.eventMappings.length);
  };

  // Remove event mapping
  const removeEventMapping = (index: number) => {
    updateWebhookConfig({
      eventMappings: webhookConfig.eventMappings.filter((_, i) => i !== index),
    });
    if (expandedEvent === index) {
      setExpandedEvent(null);
    }
  };

  // Update event mapping
  const updateEventMapping = (index: number, updates: Partial<EventMapping>) => {
    const newMappings = [...webhookConfig.eventMappings];
    newMappings[index] = { ...newMappings[index], ...updates };
    updateWebhookConfig({ eventMappings: newMappings });
  };

  // Add payload mapping to event
  const addPayloadMapping = (eventIndex: number) => {
    const event = webhookConfig.eventMappings[eventIndex];
    const newMapping: FieldMapping = { sourcePath: '', targetPath: '' };
    updateEventMapping(eventIndex, {
      payloadMapping: [...event.payloadMapping, newMapping],
    });
  };

  // Update payload mapping
  const updatePayloadMapping = (
    eventIndex: number,
    mapIndex: number,
    updates: Partial<FieldMapping>
  ) => {
    const event = webhookConfig.eventMappings[eventIndex];
    const newMappings = [...event.payloadMapping];
    newMappings[mapIndex] = { ...newMappings[mapIndex], ...updates };
    updateEventMapping(eventIndex, { payloadMapping: newMappings });
  };

  // Remove payload mapping
  const removePayloadMapping = (eventIndex: number, mapIndex: number) => {
    const event = webhookConfig.eventMappings[eventIndex];
    updateEventMapping(eventIndex, {
      payloadMapping: event.payloadMapping.filter((_, i) => i !== mapIndex),
    });
  };

  // Validate on data change
  useEffect(() => {
    // Webhooks are optional, so always valid
    // But if enabled, signature header should be set
    const isValid = !webhookConfig.enabled || !!webhookConfig.signatureHeader?.trim();
    onValidate(isValid);
  }, [webhookConfig, onValidate]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Configuração de Webhooks</h2>
        <p className="text-sm text-surface-400">
          Configure como o sistema deve receber e processar eventos do provider
        </p>
      </div>

      {/* Enable Webhooks Toggle */}
      <div className="flex items-center justify-between p-4 bg-surface-800/50 rounded-lg border border-surface-700">
        <div className="flex items-center gap-3">
          <Webhook className="w-5 h-5 text-primary-400" />
          <div>
            <p className="text-sm font-medium text-white">Habilitar Webhooks</p>
            <p className="text-xs text-surface-400">Receber notificações de eventos do provider</p>
          </div>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={webhookConfig.enabled}
            onChange={(e) => updateWebhookConfig({ enabled: e.target.checked })}
            className="sr-only"
          />
          <div className={cn(
            'w-11 h-6 rounded-full transition-colors',
            webhookConfig.enabled ? 'bg-primary-500' : 'bg-surface-600'
          )} />
          <div className={cn(
            'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform',
            webhookConfig.enabled && 'translate-x-5'
          )} />
        </label>
      </div>

      {webhookConfig.enabled && (
        <>
          {/* Signature Validation */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-surface-200">
              Validação de Assinatura
            </label>
            <div className="grid grid-cols-2 gap-3">
              {signatureTypes.map((type) => {
                const isSelected = webhookConfig.signatureValidation === type.value;
                
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => updateWebhookConfig({ signatureValidation: type.value as any })}
                    className={cn(
                      'flex flex-col items-start p-3 rounded-lg border-2 transition-all text-left',
                      isSelected
                        ? 'border-primary-500 bg-primary-500/10'
                        : 'border-surface-600 bg-surface-800 hover:border-surface-500'
                    )}
                  >
                    <span className={cn(
                      'text-sm font-medium',
                      isSelected ? 'text-white' : 'text-surface-300'
                    )}>
                      {type.label}
                    </span>
                    <span className="text-xs text-surface-500">{type.description}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Signature Header */}
          {webhookConfig.signatureValidation !== 'NONE' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-surface-300">
                  Header de Assinatura <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={webhookConfig.signatureHeader}
                  onChange={(e) => updateWebhookConfig({ signatureHeader: e.target.value })}
                  placeholder="x-webhook-signature"
                  className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                />
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-surface-300">
                  Campo do Secret (no schema)
                </label>
                <input
                  type="text"
                  value={webhookConfig.signatureSecretField || ''}
                  onChange={(e) => updateWebhookConfig({ signatureSecretField: e.target.value })}
                  placeholder="webhook_secret"
                  className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                />
                <p className="text-xs text-surface-500">
                  Nome do campo no schema de credenciais que contém o secret
                </p>
              </div>
            </div>
          )}

          {/* Event Mappings */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-surface-200">
                Mapeamento de Eventos
              </label>
            </div>

            <div className="space-y-3">
              {webhookConfig.eventMappings.map((event, index) => (
                <div
                  key={index}
                  className="border border-surface-600 rounded-lg bg-surface-800/50 overflow-hidden"
                >
                  {/* Event Header */}
                  <div
                    className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-surface-700/50"
                    onClick={() => setExpandedEvent(expandedEvent === index ? null : index)}
                  >
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                      <span className="text-sm font-mono text-surface-300 truncate">
                        {event.providerEvent || 'evento_provider'}
                      </span>
                      <ArrowRight className="w-4 h-4 text-surface-500 flex-shrink-0" />
                      <span className="text-sm font-mono text-primary-400 truncate">
                        {event.hubEvent || 'EVENTO_HUB'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeEventMapping(index); }}
                        className="p-1 text-surface-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {expandedEvent === index ? (
                        <ChevronUp className="w-4 h-4 text-surface-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-surface-400" />
                      )}
                    </div>
                  </div>

                  {/* Event Details (Expanded) */}
                  {expandedEvent === index && (
                    <div className="px-4 pb-4 pt-2 border-t border-surface-700 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        {/* Provider Event */}
                        <div className="space-y-1.5">
                          <label className="block text-xs font-medium text-surface-300">
                            Evento do Provider
                          </label>
                          <input
                            type="text"
                            value={event.providerEvent}
                            onChange={(e) => updateEventMapping(index, { providerEvent: e.target.value })}
                            placeholder="OPENPIX:CHARGE_COMPLETED"
                            className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-primary-500"
                          />
                        </div>

                        {/* Hub Event */}
                        <div className="space-y-1.5">
                          <label className="block text-xs font-medium text-surface-300">
                            Evento do Hub
                          </label>
                          <select
                            value={event.hubEvent}
                            onChange={(e) => updateEventMapping(index, { hubEvent: e.target.value })}
                            className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                          >
                            <option value="">Selecione...</option>
                            {hubEvents.map((evt) => (
                              <option key={evt} value={evt}>{evt}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Payload Mapping */}
                      <div className="space-y-2 p-3 bg-surface-900 rounded-lg">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-surface-300">Mapeamento de Payload</p>
                          <button
                            type="button"
                            onClick={() => addPayloadMapping(index)}
                            className="text-xs text-primary-400 hover:text-primary-300"
                          >
                            + Adicionar
                          </button>
                        </div>

                        {event.payloadMapping.length === 0 ? (
                          <p className="text-xs text-surface-500 italic">Nenhum mapeamento definido</p>
                        ) : (
                          <div className="space-y-2">
                            {event.payloadMapping.map((mapping, mapIndex) => (
                              <div key={mapIndex} className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={mapping.sourcePath}
                                  onChange={(e) => updatePayloadMapping(index, mapIndex, { sourcePath: e.target.value })}
                                  placeholder="webhook.charge.status"
                                  className="flex-1 px-2 py-1 bg-surface-800 border border-surface-600 rounded text-white text-xs focus:outline-none focus:border-primary-500"
                                />
                                <ArrowRight className="w-4 h-4 text-surface-500 flex-shrink-0" />
                                <input
                                  type="text"
                                  value={mapping.targetPath}
                                  onChange={(e) => updatePayloadMapping(index, mapIndex, { targetPath: e.target.value })}
                                  placeholder="status"
                                  className="flex-1 px-2 py-1 bg-surface-800 border border-surface-600 rounded text-white text-xs focus:outline-none focus:border-primary-500"
                                />
                                <button
                                  type="button"
                                  onClick={() => removePayloadMapping(index, mapIndex)}
                                  className="p-1 text-surface-400 hover:text-red-400"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Empty State */}
              {webhookConfig.eventMappings.length === 0 && (
                <div className="text-center py-6 border-2 border-dashed border-surface-600 rounded-lg">
                  <p className="text-surface-400 mb-1">Nenhum evento mapeado</p>
                  <p className="text-xs text-surface-500">
                    Adicione mapeamentos para processar eventos do provider
                  </p>
                </div>
              )}
            </div>

            {/* Add Event Button */}
            <button
              type="button"
              onClick={addEventMapping}
              className={cn(
                'flex items-center justify-center gap-2 w-full py-3 rounded-lg border-2 border-dashed transition-colors',
                'border-surface-600 text-surface-400 hover:border-primary-500 hover:text-primary-400'
              )}
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm font-medium">Adicionar Evento</span>
            </button>
          </div>

          {/* Common Events Templates */}
          <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
            <p className="text-xs font-medium text-surface-300 mb-3">Eventos Comuns (OpenPix)</p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => updateWebhookConfig({
                  eventMappings: [
                    ...webhookConfig.eventMappings,
                    {
                      providerEvent: 'OPENPIX:CHARGE_COMPLETED',
                      hubEvent: 'CHARGE_COMPLETED',
                      payloadMapping: [
                        { sourcePath: 'charge.correlationID', targetPath: 'correlationId' },
                        { sourcePath: 'charge.status', targetPath: 'status' },
                        { sourcePath: 'pix.value', targetPath: 'amount' },
                      ],
                    },
                  ],
                })}
                className="px-3 py-1.5 text-xs bg-surface-700 text-surface-300 rounded hover:bg-surface-600"
              >
                + Cobrança Paga
              </button>
              <button
                type="button"
                onClick={() => updateWebhookConfig({
                  eventMappings: [
                    ...webhookConfig.eventMappings,
                    {
                      providerEvent: 'OPENPIX:CHARGE_EXPIRED',
                      hubEvent: 'CHARGE_EXPIRED',
                      payloadMapping: [
                        { sourcePath: 'charge.correlationID', targetPath: 'correlationId' },
                      ],
                    },
                  ],
                })}
                className="px-3 py-1.5 text-xs bg-surface-700 text-surface-300 rounded hover:bg-surface-600"
              >
                + Cobrança Expirada
              </button>
              <button
                type="button"
                onClick={() => updateWebhookConfig({
                  eventMappings: [
                    ...webhookConfig.eventMappings,
                    {
                      providerEvent: 'OPENPIX:PAYMENT_CONFIRMED',
                      hubEvent: 'PAYMENT_CONFIRMED',
                      payloadMapping: [
                        { sourcePath: 'payment.correlationID', targetPath: 'correlationId' },
                        { sourcePath: 'payment.status', targetPath: 'status' },
                      ],
                    },
                  ],
                })}
                className="px-3 py-1.5 text-xs bg-surface-700 text-surface-300 rounded hover:bg-surface-600"
              >
                + Pagamento Confirmado
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default WebhookConfigStep;
